import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import Spinner from 'react-bootstrap/Spinner';
import moment from 'moment';

import { URLS } from '../../resources/constants';
import { useForecastApi } from '../../resources/hooks/api/useForecastApiHook';
import SAlert from '../../components/SAlert';
import ForecastSetupModal, { ForecastSetupValues } from './ForecastSetupModal';
import { MONTH_FULL_NAMES } from './forecastUtils';
import ForecastLayout from './ForecastLayout';

/**
 * Five-Year Forecast — list of the subscriber's forecasts.
 * Create opens the setup modal; a new forecast goes straight to Forecast Entry.
 */
const FiveYearForecast: React.FC = () => {
    const { t } = useTranslation('scenes.FiveYearForecast');
    const navigate = useNavigate();

    const { listForecasts, createForecast, deleteForecast } = useForecastApi();
    const [forecasts, setForecasts] = useState<any[]>([]);
    const [loading, setLoading] = useState<boolean>(false);
    const [showSetup, setShowSetup] = useState<boolean>(false);
    const [saving, setSaving] = useState<boolean>(false);
    const [toDelete, setToDelete] = useState<any>(null);

    const load = () => {
        setLoading(true);
        listForecasts({}, (_msg: string, resp: any) => {
            setForecasts(resp?.data?.data || []);
            setLoading(false);
        }, (msg: string) => {
            toast.error(msg);
            setLoading(false);
        });
    };

    useEffect(load, []); // eslint-disable-line react-hooks/exhaustive-deps

    const create = (values: ForecastSetupValues) => {
        setSaving(true);
        createForecast(values,
            (msg: string, resp: any) => {
                setSaving(false);
                setShowSetup(false);
                toast.success(msg);
                const id = resp?.data?.data?.id;
                if (id) navigate(URLS.FIVE_YEAR_FORECAST_ENTRY.replace(':forecastId', String(id)));
                else load();
            }, (msg: string) => {
                setSaving(false);
                toast.error(msg);
            });
    };

    const confirmDelete = () => {
        const f = toDelete;
        setToDelete(null);
        deleteForecast(f.id, (msg: string) => {
            toast.success(msg);
            load();
        }, (msg: string) => toast.error(msg));
    };

    const typeLabel = (f: any) => (f.forecast_type === 'new_location'
        ? t('type.new_location', 'New location') : t('type.new_business', 'New business'));

    return (
        <ForecastLayout active="list">
            <div className="d-flex justify-content-between align-items-start flex-wrap gap-3">
                <div>
                    <h1>{t('title', 'Five-Year Forecast')}</h1>
                    <div className="fyf-sub">
                        {t('subtitle', 'Five-year projections for a new business or a new location. Forecasts never change the client\'s books.')}
                    </div>
                </div>
                <button type="button" className="fyf-primary" onClick={() => setShowSetup(true)}>
                    {t('list.new', 'New Forecast')}
                </button>
            </div>

            <div className="fyf-panel">
                <h3>{t('list.heading', 'My Forecasts')}</h3>
                {loading ? (
                    <div className="fyf-empty"><Spinner animation="border" /></div>
                ) : forecasts.length === 0 ? (
                    <div className="fyf-empty">
                        <p className="mb-3">{t('list.empty', 'No forecasts yet.')}</p>
                        <button type="button" className="fyf-secondary" onClick={() => setShowSetup(true)}>
                            {t('list.createFirst', 'Create your first forecast')}
                        </button>
                    </div>
                ) : (
                    <div className="fyf-table-wrap">
                        <table>
                            <thead>
                                <tr>
                                    <th>{t('list.name', 'Forecast')}</th>
                                    <th>{t('list.type', 'Type')}</th>
                                    <th>{t('list.starts', 'Starts')}</th>
                                    <th>{t('list.yearType', 'Year type')}</th>
                                    <th className="text-end">{t('list.lines', 'Accounts')}</th>
                                    <th>{t('list.status', 'Status')}</th>
                                    <th>{t('list.updated', 'Last updated')}</th>
                                    <th />
                                </tr>
                            </thead>
                            <tbody>
                                {forecasts.map((f) => (
                                    <tr key={f.id}>
                                        <td>
                                            <div className="fw-bold">{f.name}</div>
                                            {f.description && <div className="fyf-meta">{f.description}</div>}
                                        </td>
                                        <td>{typeLabel(f)}</td>
                                        <td className="text-nowrap">{MONTH_FULL_NAMES[f.start_month - 1]} {f.start_year}</td>
                                        <td>{f.year_type === 'fiscal' ? t('setup.fiscal', 'Fiscal year') : t('setup.calendar', 'Calendar year')}</td>
                                        <td className="text-end">{f.line_count}</td>
                                        <td>
                                            <span className={`fyf-badge ${f.status === 'final' ? 'final' : 'draft'}`}>
                                                {f.status === 'final' ? t('status.final', 'Final') : t('status.draft', 'Draft')}
                                            </span>
                                        </td>
                                        <td className="text-nowrap">{f.updated_at ? moment(f.updated_at).format('MM/DD/YYYY') : ''}</td>
                                        <td className="text-end text-nowrap">
                                            <button type="button" className="fyf-primary fyf-sm me-1"
                                                onClick={() => navigate(URLS.FIVE_YEAR_FORECAST_ENTRY.replace(':forecastId', String(f.id)))}>
                                                {t('list.open', 'Open')}
                                            </button>
                                            <button type="button" className="fyf-danger fyf-sm" onClick={() => setToDelete(f)}>
                                                {t('list.delete', 'Delete')}
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>

            <ForecastSetupModal
                show={showSetup}
                title={t('setup.newTitle', 'New Five-Year Forecast')}
                saving={saving}
                onClose={() => setShowSetup(false)}
                onSave={create}
            />

            <SAlert
                show={!!toDelete}
                title={t('list.deleteTitle', 'Delete forecast?')}
                msg={t('list.deleteMsg', '"{{name}}" and all of its entries will be removed.', { name: toDelete?.name || '' })}
                icon="warning"
                showCancel
                confirmBtnText={t('list.delete', 'Delete')}
                cancelBtnText={t('common.cancel', 'Cancel')}
                confirmBtnBsStyle="danger"
                onConfirm={confirmDelete}
                onCancel={() => setToDelete(null)}
            />
        </ForecastLayout>
    );
};

export default FiveYearForecast;
